import { jsxs, jsx } from "react/jsx-runtime";
import { RewardUnlockStrategy } from "@brandclub/types";
import { useMediaQuery, Skeleton, Box, Typography, styled } from "@mui/material";
import { getAppConsistentParams } from "../../utils/appUtils.js";
import { resolveProductImageUrls, getEntityImage } from "../../utils/assetUtils.js";
import { getPurchaseRewardAmountForProduct, getProductUrl } from "../../utils/productUtils.js";
import { formatMoney } from "../../utils/stringUtils.js";
import { FallbackImage } from "../../Components/FallbackImage.js";
import { CampaignType } from "../../types/Campaign.js";
import { QuantityControl } from "./QuantityControl.js";
import { RewardPillWithText } from "../components/RewardPill/RewardPillWithText.js";
const CartProductContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  textDecoration: "none",
  padding: "20px 0",
  color: theme.vars.palette.text.primary,
  alignItems: "center",
  gap: 15,
  ".product_image": {
    objectFit: "contain",
    width: "92px",
    height: "104px"
  },
  ".product_detail": {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    width: "100%",
    gap: 6,
    ".info": {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      [theme.breakpoints.down("sm")]: {
        gap: 4
      },
      ".retailer_logo": {
        width: 24,
        height: 24,
        img: {
          width: 24,
          height: 24,
          objectFit: "contain"
        }
      },
      ".price": {
        display: "flex",
        alignItems: "center",
        fontSize: 16,
        fontWeight: 600,
        gap: 13,
        [`theme.breakpoints.down('sm')`]: {
          gap: 11
        }
      },
      ".name": {
        fontSize: 12,
        fontWeight: 500
      }
    },
    ".bc-actions": {
      display: "flex",
      gap: 50,
      marginTop: 5,
      alignItems: "center",
      justifyContent: "space-between",
      ".quantity": {},
      ".remove": {
        fontSize: 14,
        fontWeight: 500,
        "&:hover": {
          cursor: "pointer",
          textDecoration: "underline"
        }
      }
    }
  },
  "&.large": {
    gap: 30,
    [`theme.breakpoints.down('sm')`]: {
      gap: 25
    },
    ".product_image": {
      width: 115,
      height: 115,
      [`theme.breakpoints.down('sm')`]: {
        width: "92px",
        height: "104px"
      }
    },
    ".product_detail": {
      gap: 10,
      [`theme.breakpoints.down('sm')`]: {
        gap: 6
      },
      ".info": {
        ".price": {
          fontWeight: 600,
          fontSize: 16
        },
        ".name": {
          fontWeight: 500,
          fontSize: 14
        }
      },
      ".bc-actions": {
        justifyContent: "space-between",
        ".remove": {
          fontWeight: 500,
          fontSize: 14,
          [`theme.breakpoints.down('sm')`]: {
            fontSize: 12
          }
        }
      }
    }
  }
}));
const CartProduct = ({
  large = false,
  product,
  className,
  disabled = false,
  editable = true,
  handleAddQuantity,
  handleMinusQuantity,
  cartProducts,
  storeBrandingType
}) => {
  const cartProduct = cartProducts == null ? void 0 : cartProducts[product.stacklineSku];
  const metadata = cartProduct ?? product;
  const hasActiveInstantReward = !!(metadata == null ? void 0 : metadata.hasActiveInstantReward) || !!(metadata == null ? void 0 : metadata.campaignRewards) && (metadata == null ? void 0 : metadata.campaignRewards.some(
    (campaignReward) => (campaignReward == null ? void 0 : campaignReward.campaignType) === CampaignType.DailyDealsRewardCampaign || campaignReward && "rewardUnlockStrategy" in campaignReward && campaignReward.rewardUnlockStrategy === RewardUnlockStrategy.Instant
  ));
  const rewardAmount = metadata && getPurchaseRewardAmountForProduct(metadata);
  const rewardPercent = !!(metadata == null ? void 0 : metadata.retailPrice) && !!rewardAmount ? rewardAmount * 100 / metadata.retailPrice : 0;
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const urlSearchParams = getAppConsistentParams(window.location.search);
  if (!(metadata == null ? void 0 : metadata.title)) {
    return /* @__PURE__ */ jsxs(CartProductContainer, { className: "cart_product", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "product_image" }),
      /* @__PURE__ */ jsx(Box, { className: "product_detail", children: /* @__PURE__ */ jsxs(Box, { className: "info", children: [
        /* @__PURE__ */ jsx(Skeleton, { className: "price" }),
        /* @__PURE__ */ jsx(Skeleton, { className: "name" })
      ] }) })
    ] });
  }
  return /* @__PURE__ */ jsxs(CartProductContainer, { className: [large && "large", className].filter(Boolean).join(" ") + " cart_product", children: [
    /* @__PURE__ */ jsx(
      "a",
      {
        href: getProductUrl({
          title: metadata.title,
          stacklineSku: metadata.stacklineSku,
          urlSearchParams,
          dtcProductPageUrl: metadata.dtcProductPageUrl,
          storeType: storeBrandingType
        }),
        style: { textDecoration: "none" },
        target: "_blank",
        children: /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
          FallbackImage,
          {
            title: (metadata == null ? void 0 : metadata.title) ?? product.stacklineSku,
            className: "product_image",
            ...resolveProductImageUrls(metadata)
          }
        ) })
      }
    ),
    /* @__PURE__ */ jsxs(Box, { className: "product_detail", children: [
      /* @__PURE__ */ jsxs(Box, { className: "info", children: [
        /* @__PURE__ */ jsxs(Box, { className: "price", children: [
          /* @__PURE__ */ jsx(Box, { className: "retailer_logo", children: /* @__PURE__ */ jsx(
            "img",
            {
              src: getEntityImage(product.retailerId, "retailer"),
              alt: `Logo for retailer: ${product.retailerId}`
            }
          ) }),
          formatMoney((metadata == null ? void 0 : metadata.retailPrice) ?? product.retailPrice),
          !!rewardAmount && /* @__PURE__ */ jsx(
            RewardPillWithText,
            {
              rewardAmount,
              rewardPercentText: "reward",
              baseFontSize: isMobileView ? 12 : 14,
              pillContainerStyle: {
                boxSizing: "border-box",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center"
              },
              rewardPercent,
              rewardCampaignType: hasActiveInstantReward ? CampaignType.DailyDealsRewardCampaign : CampaignType.PurchaseRewardCampaign
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          Typography,
          {
            className: "name",
            title: metadata == null ? void 0 : metadata.title,
            sx: {
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2
            },
            children: (metadata == null ? void 0 : metadata.title) ?? "Unknown Product"
          }
        )
      ] }),
      editable && /* @__PURE__ */ jsx(Box, { className: "bc-actions", children: /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
        QuantityControl,
        {
          large,
          disabled,
          count: product.quantity,
          onAddClick: () => handleAddQuantity(product),
          onMinusClick: () => handleMinusQuantity(product),
          trackedParams: product
        }
      ) }) })
    ] })
  ] });
};
export {
  CartProduct
};
//# sourceMappingURL=CartProduct.js.map
